'use client';

import { styled } from 'styled-components';
import cs from 'classnames';
import TextLink from '../components/TextLink';
import type { CtaProps, ReactNodes } from '@types';
type ImageCardColumnsBaseProps = {
  /**
   * Headline for the introductory content
   */
  headline?: string;
  /**
   * Descriptive paragraph for the introductory content
   */
  description?: string;
  /**
   * Optional CTA for the module
   */
  cta?: CtaProps;
};

/*
 * `variant` is the discriminator: 'grid' wraps its cards into two rows — 2×2
 * with four cards, 3×2 with six — while 'row' (the default) keeps every card
 * on one line and accepts two, three, or four.
 */
type ImageCardColumnsProps = ImageCardColumnsBaseProps &
  (
    | {
        variant?: 'row';
        children: ReactNodes<2> | ReactNodes<3> | ReactNodes<4>;
      }
    | { variant: 'grid'; children: ReactNodes<4> | ReactNodes<6> }
  );

const ImageCardsContainer = styled.div`
  padding-block: ${({ theme }) => theme.spacing[3]};
  text-align: center;

  .ImageCards {
    display: flex;
    flex-direction: column;
    text-align: start;
    margin-block-start: ${({ theme }) => theme.spacing[1]};

    &.isFlush {
      margin-block-start: 0;
    }

    & > * {
      margin-block-start: ${({ theme }) => theme.spacing[2]};
    }
  }

  ${({ theme }) => theme.mq.minWidth.medium} {
    .ImageCards {
      display: grid;
      align-items: start;
      margin-block-start: ${({ theme }) => theme.spacing[3]};
      ${({ theme }) => theme.grid.columns.medium}

      & > * {
        grid-column: auto / span 6;
        margin-block-start: ${({ theme }) => theme.spacing[3]};
      }
    }
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    padding-block: ${({ theme }) => theme.spacing[4]};

    .ImageCards {
      display: grid;
      /* Cards in a row stretch to equal height so their CTAs line up along
         the bottom (see ImageCard's body/button rules). */
      align-items: stretch;
      ${({ theme }) => theme.grid.columns.large}

      & > * {
        grid-column: auto / span 4;
        margin-block-start: 0;
      }
    }

    &.two-up .ImageCards {
      row-gap: ${({ theme }) => theme.spacing[2]};
    }

    &.three-up .ImageCards {
      row-gap: ${({ theme }) => theme.spacing[2]};
    }

    &.two-up .ImageCards > * {
      grid-column: auto / span 6;
    }

    &.four-up .ImageCards {
      row-gap: ${({ theme }) => theme.spacing[2]};
    }

    &.four-up .ImageCards > * {
      grid-column: auto / span 6;
    }
  }

  ${({ theme }) => theme.mq.minWidth.xl} {
    &.four-up .ImageCards > * {
      grid-column: auto / span 3;
    }
  }
`;

/**
 * Thematically grouped image cards, with introductory context.
 */
const ImageCardColumns = (props: ImageCardColumnsProps) => {
  const { headline, description, cta = null, children } = props;
  const variant = props.variant ?? 'row';
  // A grid is always two rows, so it takes half its cards per row; a row takes
  // all of them.
  const columnsPerRow =
    variant === 'grid' ? children.length / 2 : children.length;
  const layout = { 2: 'two-up', 3: 'three-up', 4: 'four-up' }[columnsPerRow];
  const hasContextualContent = headline || description || cta;

  return (
    <ImageCardsContainer className={cs(layout)}>
      {headline ? <h2>{headline}</h2> : null}
      {description ? <p>{description}</p> : null}
      {cta ? <TextLink cta={cta} /> : null}
      <div className={cs('ImageCards', !hasContextualContent && 'isFlush')}>
        {children}
      </div>
    </ImageCardsContainer>
  );
};

export default ImageCardColumns;
